import React, { useCallback, useContext, useEffect, useState } from "react";
import { Button } from "@mui/material";
import { Feature, LineString, Position } from "geojson";
import { useSnackbar } from "notistack";

import { HistoryContext, useHistory } from "fond/history";
import { MergeIcon } from "fond/svg_icons";
import { booleanEqual, point } from "fond/turf";

import { MapContext } from "../MapProvider";

import { IButtonProps } from "./Toolbar";
import ToolbarButton from "./ToolbarButton";
import { useGeometryTypeCheck } from "./useGeometryTypeCheck";
import { useToolEnabled } from "./useToolEnabled";

/**
 * Determines if two positions (coordinate arrays) are collocated,
 * using turf's booleanEqual with precision 6 (matching codebase convention).
 */
const arePointsCollocated = (a: Position, b: Position): boolean => {
  return booleanEqual(point(a), point(b), { precision: 6 });
};

/**
 * Determines the join configuration for two LineString features.
 * Returns the merged coordinates array with the shared point deduplicated,
 * or null if the features do not share a collocated endpoint.
 */
const getJoinedCoordinates = (first: Feature<LineString>, second: Feature<LineString>): Position[] | null => {
  const firstCoords = first.geometry.coordinates;
  const secondCoords = second.geometry.coordinates;

  const firstStart = firstCoords[0];
  const firstEnd = firstCoords[firstCoords.length - 1];
  const secondStart = secondCoords[0];
  const secondEnd = secondCoords[secondCoords.length - 1];

  if (arePointsCollocated(firstEnd, secondStart)) {
    return [...firstCoords, ...secondCoords.slice(1)];
  }

  if (arePointsCollocated(firstEnd, secondEnd)) {
    return [...firstCoords, ...[...secondCoords].reverse().slice(1)];
  }

  if (arePointsCollocated(firstStart, secondEnd)) {
    return [...secondCoords, ...firstCoords.slice(1)];
  }

  if (arePointsCollocated(firstStart, secondStart)) {
    return [...[...secondCoords].reverse(), ...firstCoords.slice(1)];
  }

  return null;
};

const JoinButton: React.FC<IButtonProps> = ({ icon, title = "Join features", ...rest }) => {
  const { drawControl, map } = useContext(MapContext);
  const [isDisabled, setIsDisabled] = useState(true);
  const isLineStringLayer = useGeometryTypeCheck("LineString");
  const isToolEnabled = useToolEnabled("join");
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { undo } = useHistory(HistoryContext);

  /**
   * Monitor selection changes to enable/disable the button.
   * Enabled when exactly 2 LineString features are selected that share
   * a collocated endpoint vertex.
   */
  const handleOnSelect = useCallback(() => {
    const selectedFeatures = drawControl.current?.getSelected().features || [];

    if (selectedFeatures.length !== 2) {
      setIsDisabled(true);
      return;
    }

    const [first, second] = selectedFeatures;

    if (first.geometry?.type !== "LineString" || second.geometry?.type !== "LineString") {
      setIsDisabled(true);
      return;
    }

    const joinedCoords = getJoinedCoordinates(first as Feature<LineString>, second as Feature<LineString>);
    setIsDisabled(joinedCoords === null);
  }, [drawControl]);

  useEffect(() => {
    map?.on("draw.selectionchange", handleOnSelect);

    return () => {
      map?.off("draw.selectionchange", handleOnSelect);
    };
  }, [map, handleOnSelect]);

  const handleOnClick = () => {
    const selectedFeatures = drawControl.current?.getSelected().features || [];
    if (selectedFeatures.length !== 2) return;

    const [first, second] = selectedFeatures as Feature<LineString>[];
    const joinedCoords = getJoinedCoordinates(first, second);
    if (!joinedCoords) return;

    const joinedFeature: Feature<LineString> = {
      type: "Feature",
      id: first.id,
      properties: { ...first.properties },
      geometry: {
        type: "LineString",
        coordinates: joinedCoords,
      },
    };

    // Delete the second feature
    drawControl.current?.delete([String(second.id)]);

    // Add the modified first feature (replaces existing by ID)
    drawControl.current?.add(joinedFeature);

    // Select the joined feature
    drawControl.current?.changeMode("simple_select", { featureIds: [String(joinedFeature.id)] });

    // Fire join event for history tracking
    map?.fire("draw.join", {
      action: "join",
      features: [joinedFeature],
    });

    const snackbarKey = enqueueSnackbar("Features joined succesfully", {
      action: (
        <Button
          color="inherit"
          onClick={() => {
            undo();
            closeSnackbar(snackbarKey);
          }}
        >
          Undo
        </Button>
      ),
    });
  };

  return (
    <ToolbarButton title={title} {...rest} onClick={handleOnClick} disabled={!isLineStringLayer || !isToolEnabled || isDisabled}>
      {icon || <MergeIcon />}
    </ToolbarButton>
  );
};

export default JoinButton;
